import React, { useState, useRef, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import MobileDialog from './MobileDialog';
import PaymentMethodRows from '../components/PaymentMethodRows';
import { createCashDeskSubmission } from '../../services/cashflowService';

const CashDeskSubmissionDialog = ({
  open,
  onClose,
  onSuccess,
  cashDesks = [],
}) => {
  // Form state
  const [selectedCashDeskId, setSelectedCashDeskId] = useState('');
  const [periodType, setPeriodType] = useState('daily');
  const [periodDate, setPeriodDate] = useState(new Date());
  const [amount, setAmount] = useState('');
  const [employeeExpenses, setEmployeeExpenses] = useState('');
  const [note, setNote] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);

  // UI state
  const [formStep, setFormStep] = useState('form');
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const errorRef = useRef(null);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (localError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [localError]);

  const resetForm = () => {
    setSelectedCashDeskId('');
    setPeriodType('daily');
    setPeriodDate(new Date());
    setAmount('');
    setEmployeeExpenses('');
    setNote('');
    setPaymentMethods([]);
    setFormStep('form');
    setLocalError('');
    setLocalSuccess('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleValidation = () => {
    setLocalError('');
    setLocalSuccess('');

    if (!selectedCashDeskId) {
      setLocalError('שגיאה: יש לבחור קופה.');
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setLocalError('שגיאה: הסכום חייב להיות גדול מאפס.');
      return;
    }

    if (employeeExpenses && parseFloat(employeeExpenses) < 0) {
      setLocalError('שגיאה: הוצאות עובדים לא יכולות להיות שליליות.');
      return;
    }

    // Validate payment methods
    if (paymentMethods.length > 0) {
      const pmTotal = paymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
      if (Math.abs(pmTotal - parsedAmount) > 0.01) {
        setLocalError('שגיאה: סכום אמצעי התשלום אינו תואם לסכום הכולל.');
        return;
      }

      const invalidChecks = paymentMethods.filter(
        pm => pm.type === 'check' && !pm.check_number
      );
      if (invalidChecks.length > 0) {
        setLocalError('שגיאה: יש להזין מספר צ\'ק לכל תשלום בצ\'ק.');
        return;
      }
    }

    setFormStep('confirm');
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setLocalError('');
    setLocalSuccess('');

    try {
      const parsedAmount = parseFloat(amount);

      const payload = {
        CashDeskID: selectedCashDeskId,
        PeriodType: periodType,
        PeriodDate: periodDate,
        Amount: parsedAmount,
        EmployeeExpenses: employeeExpenses ? parseFloat(employeeExpenses) : null,
        Note: note,
        payment_methods: paymentMethods.length > 0 ? paymentMethods.map(pm => ({
          type: pm.type,
          amount: parseFloat(pm.amount) || 0,
          check_number: pm.check_number || '',
          check_bank_name: pm.check_bank_name || '',
          check_account_number: pm.check_account_number || '',
          check_due_date: pm.check_due_date || null,
          card_last_four: pm.card_last_four || '',
          card_type: pm.card_type || '',
          bank_name: pm.bank_name || '',
          bank_account_last4: pm.bank_account_last4 || '',
          transfer_date: pm.transfer_date || null,
          notes: pm.notes || '',
        })) : [],
      };

      await createCashDeskSubmission(payload);
      setLocalSuccess('דיווח הקופה נרשם בהצלחה!');

      queryClient.refetchQueries({ queryKey: ['cashdesk-submissions'] });
      queryClient.refetchQueries({ queryKey: ['cashdesk-metrics'] });
      queryClient.refetchQueries({ queryKey: ['cashflow'] });
      queryClient.refetchQueries({ queryKey: ['cashflow-summary'] });
      queryClient.refetchQueries({ queryKey: ['general-transactions'] });

      if (onSuccess) onSuccess();

      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('שגיאה בהוספת דיווח קופה:', err);
      setLocalError(
        err?.response?.data?.error ||
        err.message ||
        'שגיאה בעת הוספת דיווח הקופה'
      );
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  const selectedCashDesk = cashDesks.find(cd => cd.CashDeskID === selectedCashDeskId);

  return (
    <MobileDialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') handleClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '1.4rem' }}>
        דיווח הכנסה מקופה
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {localError && (
            <Alert ref={errorRef} severity="error">{localError}</Alert>
          )}
          {localSuccess && (
            <Alert severity="success">{localSuccess}</Alert>
          )}

          {formStep === 'form' && (
            <>
              {/* Cash Desk Selection */}
              <FormControl fullWidth>
                <InputLabel>קופה</InputLabel>
                <Select
                  value={selectedCashDeskId}
                  onChange={(e) => setSelectedCashDeskId(e.target.value)}
                  label="קופה"
                >
                  {cashDesks.filter(cd => cd.IsActive).map((cd) => (
                    <MenuItem key={cd.CashDeskID} value={cd.CashDeskID}>
                      {cd.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Period Type */}
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  סוג תקופה
                </Typography>
                <ToggleButtonGroup
                  value={periodType}
                  exclusive
                  onChange={(e, val) => { if (val) setPeriodType(val); }}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="daily">יומי</ToggleButton>
                  <ToggleButton value="monthly">חודשי</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Date */}
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
                <DatePicker
                  label="תאריך"
                  value={periodDate}
                  onChange={(newValue) => setPeriodDate(newValue)}
                  views={periodType === 'monthly' ? ['year', 'month'] : undefined}
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                />
              </LocalizationProvider>

              {/* Amount */}
              <TextField
                label="סכום"
                type="number"
                fullWidth
                sx={{
                  direction: 'ltr',
                  '& input': {
                    textAlign: 'center',
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '-moz-appearance': 'textfield',
                  },
                }}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onWheel={(e) => e.target.blur()}
                onFocus={(e) => e.target.select()}
              />

              {/* Employee Expenses */}
              <TextField
                label="הוצאות עובדים (אופציונלי)"
                type="number"
                fullWidth
                helperText="לצורך מעקב בלבד - לא יוצר הוצאה"
                sx={{
                  direction: 'ltr',
                  '& input': {
                    textAlign: 'center',
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '-moz-appearance': 'textfield',
                  },
                }}
                value={employeeExpenses}
                onChange={(e) => setEmployeeExpenses(e.target.value)}
                onWheel={(e) => e.target.blur()}
                onFocus={(e) => e.target.select()}
              />

              {/* Note */}
              <TextField
                label="הערות (אופציונלי)"
                type="text"
                multiline
                minRows={2}
                fullWidth
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />

              {/* Payment Methods */}
              <PaymentMethodRows
                paymentMethods={paymentMethods}
                setPaymentMethods={setPaymentMethods}
                totalAmount={parseFloat(amount) || 0}
                compact={false}
              />
            </>
          )}

          {formStep === 'confirm' && (
            <>
              <Typography sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 1 }}>
                אישור דיווח קופה
              </Typography>
              {selectedCashDesk && (
                <Typography>קופה: {selectedCashDesk.Name}</Typography>
              )}
              <Typography>סוג תקופה: {periodType === 'daily' ? 'יומי' : 'חודשי'}</Typography>
              <Typography>
                תאריך: {periodType === 'monthly'
                  ? format(periodDate, 'MMMM yyyy', { locale: he })
                  : periodDate?.toLocaleDateString('he-IL')}
              </Typography>
              <Typography sx={{ fontWeight: 'bold' }}>
                סכום: ₪{parseFloat(amount).toFixed(2)}
              </Typography>
              {employeeExpenses && (
                <Typography>
                  הוצאות עובדים: ₪{parseFloat(employeeExpenses).toFixed(2)}
                  {' '}({(parseFloat(employeeExpenses) / parseFloat(amount) * 100).toFixed(1)}%)
                </Typography>
              )}
              {note && <Typography>הערות: {note}</Typography>}

              {paymentMethods.length > 0 && (
                <>
                  <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                    אמצעי תשלום:
                  </Typography>
                  {paymentMethods.map((pm, index) => {
                    const typeLabels = {
                      cash: 'מזומן',
                      check: 'צ\'ק',
                      credit_card: 'כרטיס אשראי',
                      bank_transfer: 'העברה בנקאית',
                      other: 'אחר'
                    };
                    return (
                      <Typography key={index}>
                        {typeLabels[pm.type] || pm.type}: ₪{parseFloat(pm.amount).toFixed(2)}
                      </Typography>
                    );
                  })}
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {!localSuccess && (
          <Button
            onClick={handleClose}
            color="inherit"
            size="large"
            sx={{ fontSize: '1rem' }}
          >
            ביטול
          </Button>
        )}

        {formStep === 'form' && !localSuccess && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ fontSize: '1rem' }}
            onClick={handleValidation}
          >
            הבא
          </Button>
        )}

        {formStep === 'confirm' && !localSuccess && (
          <>
            <Button
              color="inherit"
              size="large"
              sx={{ fontSize: '1rem' }}
              onClick={() => setFormStep('form')}
            >
              חזרה
            </Button>
            <Button
              variant="contained"
              color="success"
              size="large"
              sx={{ fontSize: '1rem' }}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'אישור'}
            </Button>
          </>
        )}
      </DialogActions>
    </MobileDialog>
  );
};

export default CashDeskSubmissionDialog;
